// Custom element that draws a smooth curve cleanly through the center of every card node inside it.
// It measures the real layout (scale-aware, so it also works inside the zoomed editor preview frames)
// and redraws on resize, so the path stays glued to the cards on every viewport — including mobile.
//
// The reveal choreography (path drawing top-to-bottom + cards spinning in as the draw head reaches
// them) is driven from JS scroll progress rather than CSS `animation-timeline: view()`. That keeps the
// effect identical on every published browser, including iOS Safari where scroll-driven CSS timelines
// are not available.

interface Point {
  x: number;
  y: number;
}

const smoothPath = (points: Point[]): string => {
  if (points.length < 2) return '';
  const first = points[0]!;
  let d = `M ${first.x.toFixed(1)} ${first.y.toFixed(1)}`;
  const tension = 0.2;

  for (let index = 0; index < points.length - 1; index += 1) {
    const previous = points[index - 1] ?? points[index]!;
    const current = points[index]!;
    const next = points[index + 1]!;
    const afterNext = points[index + 2] ?? next;

    const control1x = current.x + (next.x - previous.x) * tension;
    const control1y = current.y + (next.y - previous.y) * tension;
    const control2x = next.x - (afterNext.x - current.x) * tension;
    const control2y = next.y - (afterNext.y - current.y) * tension;

    d += ` C ${control1x.toFixed(1)} ${control1y.toFixed(1)}, ${control2x.toFixed(1)} ${control2y.toFixed(1)}, ${next.x.toFixed(1)} ${next.y.toFixed(1)}`;
  }

  return d;
};

const clamp01 = (value: number) => (value < 0 ? 0 : value > 1 ? 1 : value);

class VpPathFlowElement extends HTMLElement {
  private observer?: ResizeObserver;
  private intersection?: IntersectionObserver;
  private visible = false;
  private frame = 0;
  private progressFrame = 0;
  // Layout-space centers of every node, paired 1:1 with the cards (same DOM order).
  private nodeCenters: Point[] = [];
  private cards: HTMLElement[] = [];
  private pathLength = 0;
  // Editor preview frames and reduced-motion users get the finished state instead of the scroll show.
  private isStatic = false;

  connectedCallback() {
    this.isStatic =
      this.hasAttribute('data-editor-static') ||
      this.ownerDocument.body.classList.contains('vp-editor-preview-document') ||
      !!this.closest('.vp-editor-frame') ||
      (typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);

    // Opting in via a class (only when JS is live) means a failed/disabled script leaves every card and
    // the full path visible instead of permanently hidden.
    if (!this.isStatic) this.classList.add('is-scroll-animated');

    this.scheduleDraw();
    if (typeof ResizeObserver !== 'undefined') {
      this.observer = new ResizeObserver(() => this.scheduleDraw());
      this.observer.observe(this);
      this.querySelectorAll<HTMLElement>('.vp-path-card').forEach(card => this.observer?.observe(card));
    }
    window.addEventListener('resize', this.scheduleDraw, { passive: true });
    if (!this.isStatic) {
      // Keep the journey's layout/SVG reads out of unrelated hero animations.
      // The margin resumes the same scroll state before the journey is visible.
      this.intersection = new IntersectionObserver(entries => {
        this.visible = entries.some(entry => entry.isIntersecting);
        if (this.visible) this.scheduleProgress();
      }, { rootMargin: '160px 0px' });
      this.intersection.observe(this);
      // Capture phase so we also react to scrolling inside any nested scroll container.
      window.addEventListener('scroll', this.scheduleProgress, { passive: true, capture: true });
    }
    // Fonts loading after first paint shift node positions; redraw once they are ready.
    (document as Document & { fonts?: FontFaceSet }).fonts?.ready.then(() => this.scheduleDraw()).catch(() => {});
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.observer = undefined;
    this.intersection?.disconnect();
    window.removeEventListener('resize', this.scheduleDraw);
    window.removeEventListener('scroll', this.scheduleProgress, { capture: true } as EventListenerOptions);
    if (this.frame) cancelAnimationFrame(this.frame);
    if (this.progressFrame) cancelAnimationFrame(this.progressFrame);
    this.frame = 0;
    this.progressFrame = 0;
  }

  private scheduleDraw = () => {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.draw());
  };

  private scheduleProgress = () => {
    if (!this.visible) return;
    if (this.progressFrame) cancelAnimationFrame(this.progressFrame);
    this.progressFrame = requestAnimationFrame(() => this.updateProgress());
  };

  private draw() {
    this.frame = 0;
    const svg = this.querySelector<SVGSVGElement>('.vp-path-track');
    const drawLine = this.querySelector<SVGPathElement>('.vp-path-line-draw');
    const ghostLine = this.querySelector<SVGPathElement>('.vp-path-line-ghost');
    this.cards = Array.from(this.querySelectorAll<HTMLElement>('.vp-path-card'));
    const nodes = Array.from(this.querySelectorAll<HTMLElement>('.vp-path-node'));
    if (!svg || !drawLine || !ghostLine || nodes.length < 2) return;

    const width = this.offsetWidth;
    const height = this.offsetHeight;
    if (!width || !height) return;

    // Measure from layout offsets, not getBoundingClientRect: a card waiting in its hidden reveal
    // state is CSS-transformed, and bounding rects include transforms — offsets give the true resting
    // layout position so the path stays glued to where the cards actually land. Offsets are also in
    // layout pixels (transform-independent), so the editor's scaled preview frame needs no correction.
    this.nodeCenters = nodes.map(node => this.layoutCenter(node));

    const d = smoothPath(this.nodeCenters);
    svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
    svg.setAttribute('preserveAspectRatio', 'none');
    drawLine.setAttribute('d', d);
    ghostLine.setAttribute('d', d);
    this.pathLength = typeof drawLine.getTotalLength === 'function' ? drawLine.getTotalLength() : 0;

    if (this.isStatic) {
      // Editor / reduced motion: render the finished journey straight away.
      drawLine.style.strokeDashoffset = '0';
      this.cards.forEach(card => card.classList.add('is-revealed'));
      const comet = this.querySelector<SVGCircleElement>('.vp-path-comet');
      if (comet) comet.style.opacity = '0';
      return;
    }

    this.updateProgress();
  }

  // Centre of a node in the host's layout-pixel space, summed from the offset chain so CSS transforms
  // on the cards (the reveal state) and the editor's preview scale never distort the measurement.
  private layoutCenter(node: HTMLElement): Point {
    let x = 0;
    let y = 0;
    let element: HTMLElement | null = node;
    while (element && element !== this) {
      x += element.offsetLeft;
      y += element.offsetTop;
      element = element.offsetParent as HTMLElement | null;
    }
    return { x: x + node.offsetWidth / 2, y: y + node.offsetHeight / 2 };
  }

  private updateProgress() {
    this.progressFrame = 0;
    if (this.isStatic) return;
    const drawLine = this.querySelector<SVGPathElement>('.vp-path-line-draw');
    if (!drawLine) return;

    const rect = this.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    if (!viewportHeight) return;

    // Start drawing once the block's top crosses ~82% down the viewport, and complete after the visitor
    // has scrolled through ~80% of its height — so the draw stays tied to the scroll gesture.
    const start = viewportHeight * 0.82;
    const distance = Math.max(rect.height * 0.8, viewportHeight * 0.4);
    const progress = clamp01((start - rect.top) / distance);

    drawLine.style.strokeDashoffset = String(1 - progress);

    const comet = this.querySelector<SVGCircleElement>('.vp-path-comet');
    const head =
      this.pathLength > 0 && typeof drawLine.getPointAtLength === 'function'
        ? drawLine.getPointAtLength(progress * this.pathLength)
        : null;

    if (comet) {
      if (head) {
        comet.setAttribute('cx', head.x.toFixed(1));
        comet.setAttribute('cy', head.y.toFixed(1));
      }
      // Only show the glowing draw head while the line is actively being traced.
      comet.style.opacity = progress > 0.002 && progress < 0.998 ? '1' : '0';
    }

    // Reveal each card the moment the draw head reaches (just before) its node. Below the start
    // threshold (progress 0) nothing reveals, so a section sitting under the fold stays hidden until
    // it is actually scrolled to — the draw head begins at the first node, hence the small guard.
    const lead = 36;
    this.cards.forEach((card, index) => {
      const center = this.nodeCenters[index];
      if (!center) return;
      const reached = head ? head.y >= center.y - lead : progress >= (index + 0.5) / this.cards.length;
      if (progress >= 0.998 || (progress > 0.001 && reached)) card.classList.add('is-revealed');
    });
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('vp-path')) {
  customElements.define('vp-path', VpPathFlowElement);
}
